import {
  ExcelIconSVG,
  EyeSVG,
  InputSearchIconSVG,
  PDFIconSVG,
} from "@/assets/svg";
import DropdownButton from "@/components/DropdownButton";
import { FilterSelectWithRadio } from "@/components/form";
import AppLayout from "@/components/layout/AppLayout";
import NoDataContainer from "@/components/NoDataContainer";
import Tooltip from "@/components/Tooltip";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  fetchClientOptions,
  selectClientOptions,
} from "@/store/slices/clientSlice";
import {
  fetchProjectTracking,
  getProjectTrackingDetail,
  resetFilters,
  selectClientFilter,
  selectFilteredProjects,
  selectNoProjects,
  selectPagination,
  selectSearchTerm,
  selectStatusFilter,
  selectProjectType,
  setClientFilter,
  setSearchTerm,
  setStatusFilter,
  setProjectType,
} from "@/store/slices/projectTrackingSlice";
import { ProjectList, ProjectQuery } from "@/types/project.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import { downloadProjectReport } from "@/service/project.service";
import { errorToast, successToast } from "@/utils/toast";
import moment from "moment";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import debounce from "lodash/debounce";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import Tabs from "@/components/Tabs";
import { selectUserRole } from "@/store/slices/authSlice";
import { ROLES } from "@/utils/constants";

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
  maxWidth?: string;
};

// Define status badge styles for different project statuses
const getStatusStyles = (
  status: string
): { bgColor: string; textColor: string } => {
  let bgColor = "bg-[#DCF1FE]";
  let textColor = "text-[#007FFF]";

  switch (status) {
    case "active":
      bgColor = "bg-[#EDFFF9]";
      textColor = "text-[#0C7453]";
      break;
    case "inactive":
      bgColor = "bg-[#FFEDED]";
      textColor = "text-[#FF0000]";
      break;
    case "On Hold":
      bgColor = "bg-[#FFF8ED]";
      textColor = "text-[#D88C0C]";
      break;
    case "Cancelled":
      bgColor = "bg-[#FFEDF0]";
      textColor = "text-[#FF0000]";
      break;
  }

  return { bgColor, textColor };
};

// Hours display component with color coding
const HoursDisplay: React.FC<{
  hours: number | string;
  isNegative?: boolean;
}> = ({ hours, isNegative = false }) => {
  const textColor = isNegative ? "text-red-500" : "";

  if (hours === null || hours === undefined) {
    return <span className={textColor}>-</span>;
  }

  if (typeof hours === "string") {
    const t = hours.trim();
    if (t === "" || t === "-") {
      return <span className={textColor}>-</span>;
    }
    if (/\bhrs\b/i.test(t)) {
      return <span className={textColor}>{hours}</span>;
    }
  }

  const displayHours = `${typeof hours === "number" ? hours.toFixed(2) : hours} hrs`;
  return <span className={textColor}>{displayHours}</span>;
};

// Project table component using TanStack React Table
const ProjectTable: React.FC<{
  data: ProjectList[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
  onViewBreakdown: (projectId: string) => void;
}> = ({ data, pagination, onPageChange, onViewBreakdown }) => {
  const columnHelper = createColumnHelper<ProjectList>();

  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Project name",
        cell: (info) => (
          <div
            title={info.getValue()}
            style={{
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "236px", maxWidth: "236px" } as ColumnMeta,
      }),
      columnHelper.accessor("client.name", {
        header: "Client name",
        cell: (info) => (
          <div
            title={info.getValue()}
            style={{
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "178px", maxWidth: "178px" } as ColumnMeta,
      }),

      columnHelper.accessor("esti_hrs", {
        header: "Estimated hours",
        cell: (info) => <HoursDisplay hours={info.getValue() as number} />,
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.accessor("util_hrs", {
        header: "Utilized hours",
        cell: (info) => <HoursDisplay hours={info.getValue() || 0} />,
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.accessor("remi_hrs", {
        header: "Remaining hours",
        cell: (info) => {
          const isNegative = (info.getValue() as string).includes("-");
          return (
            <HoursDisplay
              hours={info.getValue() as number}
              isNegative={isNegative}
            />
          );
        },
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.accessor("start_date", {
        header: "Start date",
        cell: (info) => {
          return moment(info.getValue(), "DD/MM/YYYY").format("MM/DD/YYYY");
        },
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.accessor("end_date", {
        header: "End date",
        cell: (info) => {
          const startDate = moment(info.row.original.start_date, "DD/MM/YYYY");
          const endDate = moment(info.getValue(), "DD/MM/YYYY");
          const isSameDate = startDate.isSame(endDate, "day");
          const textColor = isSameDate ? "text-[#FF3B30]" : "";
          return (
            <span className={textColor}>{endDate.format("MM/DD/YYYY")}</span>
          );
        },
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const { bgColor, textColor } = getStatusStyles(status);
          return (
            <span
              className={`inline-block px-2 py-1 rounded-sm text-xs font-medium ${bgColor} ${textColor}`}
            >
              {capitalizeFirstLetter(status)}
            </span>
          );
        },
        meta: { width: "144px" } as ColumnMeta,
      }),
      columnHelper.display({
        id: "month_wise_breakdown",
        header: "Month wise breakdown",
        cell: (info) => (
          <div className="flex items-center">
            <Tooltip content="View">
              <button
                className="cursor-pointer"
                onClick={() => onViewBreakdown(info.row.original.id)}
              // onClick={() => {
              //   navigate(`/reports/project-tracking/month-breakdown`);
              // }}
              >
                <EyeSVG />
              </button>
            </Tooltip>
          </div>
        ),
        meta: { width: "200px", isSticky: true } as ColumnMeta,
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <>
      <div className="overflow-x-auto relative py-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#F4F7F9] text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = (header.column.columnDef.meta as any)
                    ?.isSticky;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={{
                        width: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        minWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        maxWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                      }}
                      className={`p-3 text-sm font-[600] text-[#1E1E1E] ${isSticky
                          ? "sticky right-0 bg-[#F4F7F9] z-10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                          : ""
                        }`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No projects found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#F9FEFF]">
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = (cell.column.columnDef.meta as ColumnMeta)
                      ?.isSticky;
                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          minWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                        }}
                        className={`whitespace-nowrap text-sm text-[#1E1E1E] font-[500] ${isSticky ? "sticky right-0 my-0.5 z-10" : "px-3 py-4"
                          }`}
                      >
                        {isSticky ? (
                          <div className="p-3 bg-white">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
        <div>
          Showing{" "}
          {pagination.currentPage === 1
            ? 1
            : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.totalItems
          )}{" "}
          of {pagination.totalItems} results
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (pagination.currentPage > 1) {
                onPageChange(pagination.currentPage - 1);
              }
            }}
            disabled={pagination.currentPage <= 1}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>
          <button
            onClick={() => {
              if (pagination.currentPage < pagination.totalPages) {
                onPageChange(pagination.currentPage + 1);
              }
            }}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

const ProjectTrackingReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userRole = useAppSelector(selectUserRole);
  const isClient = userRole === ROLES.CLIENT;
  // Get project tracking data from Redux store
  const filteredProjects = useAppSelector(selectFilteredProjects);
  const searchTerm = useAppSelector(selectSearchTerm);
  const clientFilter = useAppSelector(selectClientFilter);
  const statusFilter = useAppSelector(selectStatusFilter);
  const pagination = useAppSelector(selectPagination);
  const noProjects = useAppSelector(selectNoProjects);
  const clientFilterList = useAppSelector(selectClientOptions);
  const projectType = useAppSelector(selectProjectType);

  // Track the currently applied filter values for reset functionality
  const [currentClientFilter, setCurrentClientFilter] = useState("All clients");
  const [currentStatusFilter, setCurrentStatusFilter] =
    useState("All statuses");

  // Update filter state and trigger API call when filters change
  useEffect(() => {
    // Only trigger API call if the component has mounted (prevents double API call on init)
    if (initialDataLoaded.current) {
      setCurrentClientFilter(clientFilter);
      setCurrentStatusFilter(statusFilter);

      dispatch(
        fetchProjectTracking({
          page: 1, // Reset to first page when applying filters
          pageSize: pagination.pageSize,
          search: searchTerm || undefined,
          clientId: clientFilter !== "All clients" ? clientFilter : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,

        })
      );
    }
  }, [clientFilter, statusFilter, dispatch, pagination.pageSize, searchTerm, projectType]);

  // Use ref to track if initial data has been loaded
  const initialDataLoaded = useRef(false);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        fetchProjectTracking({
          page: 1, // Reset to first page when searching
          pageSize: pagination.pageSize,
          search: searchValue || undefined,
          clientId: clientFilter !== "All clients" ? clientFilter : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,
        })
      );
    }, 500),
    [dispatch, pagination.pageSize, clientFilter, statusFilter]
  );

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Fetch projects on component mount (only once)
  useEffect(() => {
    if (!initialDataLoaded.current) {
      dispatch(fetchProjectTracking({ page: 1, pageSize: 20 }));
      dispatch(fetchClientOptions());
      initialDataLoaded.current = true;
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  // Reset all filters and fetch projects with no filters
  const handleResetFilter = () => {
    // Reset all filters
    dispatch(resetFilters());

    // Reset current filters to default values
    setCurrentClientFilter("All clients");
    setCurrentStatusFilter("All statuses");

    dispatch(
      fetchProjectTracking({
        page: 1,
        pageSize: pagination.pageSize,
      })
    );
  };

  const onViewBreakdown = (projectId: string) => {
    dispatch(getProjectTrackingDetail(projectId))
      .unwrap()
      .then(() => {
        navigate(`/reports/project-tracking/month-breakdown/${projectId}`);
      });
  };

  // Handle export of project tracking report as Excel or PDF
  const handleExport = async (type: "csv" | "pdf") => {
    try {
      // Create query params from current filters
      const queryParams: ProjectQuery = {
        page: 1,
        pageSize: pagination.pageSize,
        search: searchTerm || undefined,
        clientId: clientFilter !== "All clients" ? clientFilter : undefined,
        status:
          statusFilter !== "All statuses"
            ? statusFilter.toLowerCase()
            : undefined,
        projectType: projectType,
      };

      // downloadProjectReport handles removing empty properties internally
      const response = await downloadProjectReport(type, queryParams);

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: type === "csv" ? "text/csv" : "application/pdf",
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      const formatType = type === "csv" ? "csv" : "pdf";

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `project-tracking_report.${formatType}`);

      // Append the anchor to the body
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successToast(
        "Export Successful",
        `Project tracking report exported as ${type.toUpperCase()} successfully`
      );
    } catch (error) {
      console.error(
        `Error exporting project tracking report as ${type}:`,
        error
      );
    }
  };

  return (
    <AppLayout
      title="Project tracking report"
      description="Monitor project-level time usage month-wise"
      contentClassName={`min-h-[90%] ${pagination.totalPages ? "" : ""
        }`}
      rightActions={
        <>
          <DropdownButton
            label="Export as"
            disabled={pagination.totalItems === 0}
            options={[
              {
                icon: <ExcelIconSVG className="w-5 h-5" />,
                label: "CSV file",
                onClick: () => handleExport("csv"),
              },
              {
                icon: <PDFIconSVG className="w-5 h-5" />,
                label: "PDF file",
                onClick: () => handleExport("pdf"),
              },
            ]}
            buttonClassName="h-[38px]"
          />
        </>
      }
    >
      <div className="p-4 w-full">
        <Tabs
          items={[
            { label: "Billable", value: "billable" },
            {
              label: isClient ? "Fixed Scope" : "Non-billable",
              value: "non-billable",
            },
            { label: "Block of time", value: "block of time" },
          ]}
          value={projectType}
          onChange={(val) => dispatch(setProjectType(val))}
          className="mb-4"
        />
        {noProjects ? (
          <NoDataContainer
            title="No projects found"
            description="There are no projects in the system yet."
          />
        ) : (
          <div className="bg-white rounded-lg">
            {/* Filters */}
            <div className="py-4 flex flex-wrap items-center justify-between gap-2">
              <div className="relative flex-grow max-w-[246px]">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <InputSearchIconSVG />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-3 mr-3">
                  <div className="w-[142px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All clients", label: "All clients" },
                        ...clientFilterList.map((client) => ({
                          value: client.id, // Use org id instead of user.id
                          label: capitalizeFirstLetter(client.user.full_name),
                        })),
                      ]}
                      value={clientFilter}
                      width="w-[220px]"
                      maxHeight="250px"
                      showSearch
                      searchPlaceholder="Search clients"
                      onChange={(value) => dispatch(setClientFilter(value))}
                    />
                  </div>

                  <div className="w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All statuses", label: "All statuses" },
                        { value: "Active", label: "Active" },
                        { value: "Completed", label: "Completed" },
                      ]}
                      value={statusFilter}
                      onChange={(value) => dispatch(setStatusFilter(value))}
                    />
                  </div>
                </div>

                <button
                  className={`h-[38px] px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] ${currentClientFilter !== "All clients" ||
                      currentStatusFilter !== "All statuses"
                      ? "hover:bg-gray-50 cursor-pointer"
                      : "opacity-50 cursor-not-allowed"
                    }`}
                  onClick={handleResetFilter}
                  disabled={
                    currentClientFilter === "All clients" &&
                    currentStatusFilter === "All statuses"
                  }
                >
                  Reset filter
                </button>
              </div>
            </div>

            {/* Table with TanStack React Table */}
            <ProjectTable
              data={filteredProjects}
              pagination={pagination}
              onPageChange={(page) => {
                dispatch(
                  fetchProjectTracking({
                    page,
                    pageSize: pagination.pageSize,
                  })
                );
              }}
              onViewBreakdown={onViewBreakdown}
            />
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ProjectTrackingReport;
